import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Start.css';
import packageJson from '../../package.json';

type NavTab = 'tuner' | 'setup' | 'learn-guitar' | 'about';

interface StartProps {
  onNavigate: (tab: NavTab) => void;
}

// Phones stack the layout; below this width the intro terminal collapses into
// its own tile so the four action tiles stay above the fold.
const MOBILE_QUERY = window.matchMedia('(max-width: 768px)');

const CHAR_MS = 32;
const PAUSE_MS = 450;   // pause after a line finishes before the next starts
const BTN_DELAY = 600;  // extra wait after last line before button appears

function playBeep(ctx: AudioContext, accent = false) {
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = accent ? 1400 : 900;
    const t = ctx.currentTime;
    gain.gain.setValueAtTime(0.001, t);
    gain.gain.linearRampToValueAtTime(accent ? 0.22 : 0.14, t + 0.006);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.14);
  } catch { /* autoplay blocked */ }
}

export default function Start({ onNavigate }: StartProps) {
  const { t } = useTranslation(['game', 'common']);

  const steps: Array<{ text: string; nav: NavTab | null; url?: string }> = [
    { text: t('game:start_step1'), nav: 'tuner'        },
    { text: t('game:start_step2'), nav: 'setup'        },
    { text: t('game:start_step3'), nav: 'learn-guitar' },
    { text: t('game:start_step4'), nav: null           },
    { text: t('game:start_step5'), nav: null, url: 'https://ko-fi.com/bassmaster' },
  ];

  // App-icon tiles for the right column. Titles reuse the already-translated
  // tab labels (leading emoji stripped, since each tile shows a jumbo glyph of
  // its own); only the short descriptions are new i18n keys.
  // Strip the leading emoji cluster (pictographic + variation selectors / ZWJ +
  // the following space). \p{L}-based stripping isn't reliable here — some tab
  // glyphs (e.g. ℹ️ U+2139) aren't classified as symbols, so target emoji directly.
  const stripGlyph = (s: string) =>
    s.replace(/^\p{Extended_Pictographic}\S*\s+/u, '').trim();
  const [lineIdx, setLineIdx]       = useState(-1);    // line being typed
  const [charIdx, setCharIdx]       = useState(0);     // chars shown
  const [checked, setChecked]       = useState<boolean[]>([false, false, false, false, false]);
  const [showBtn, setShowBtn]       = useState(false);
  const [isMobile, setIsMobile]     = useState(() => MOBILE_QUERY.matches);
  const [introOpen, setIntroOpen]   = useState(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const cancelRef   = useRef(false);
  const playedRef   = useRef(false);

  // Desktop always shows the terminal; on a phone it's behind the intro tile.
  const terminalVisible = !isMobile || introOpen;

  useEffect(() => {
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    MOBILE_QUERY.addEventListener('change', onChange);
    return () => MOBILE_QUERY.removeEventListener('change', onChange);
  }, []);

  // Tile order follows the practice flow: tune → play → how it works →
  // configure → info. The intro tile only exists on phones, where it stands in
  // for the terminal card that is collapsed behind it.
  type NavTile   = { kind: 'nav'; nav: NavTab; glyph: string; title: string; desc: string };
  type IntroTile = { kind: 'intro'; glyph: string; title: string; desc: string };

  const introTile: IntroTile = {
    kind: 'intro',
    glyph: '🚀',
    title: t('game:start_tile_intro_title'),
    desc:  t('game:start_tile_intro_desc', { steps: steps.length }),
  };
  const tiles: Array<NavTile | IntroTile> = [
    { kind: 'nav', nav: 'tuner',        glyph: '🎚️', title: stripGlyph(t('common:tab_tuner')),        desc: t('game:start_tile_tuner_desc') },
    { kind: 'nav', nav: 'learn-guitar', glyph: '🎮', title: stripGlyph(t('common:tab_learn_guitar')),  desc: t('game:start_tile_learn_desc') },
    ...(isMobile ? [introTile] : []),
    { kind: 'nav', nav: 'setup',        glyph: '⚙️', title: stripGlyph(t('common:tab_setup')),         desc: t('game:start_tile_setup_desc') },
    { kind: 'nav', nav: 'about',        glyph: 'ℹ️', title: stripGlyph(t('common:tab_about')),          desc: t('game:start_tile_about_desc') },
  ];

  // Unlock AudioContext on first gesture (pointer or key anywhere on the page)
  useEffect(() => {
    const unlock = () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (audioCtxRef.current.state === 'suspended') {
        void audioCtxRef.current.resume();
      }
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown',    unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown',    unlock);
    };
  }, []);

  // Typewriter animation. On a phone it waits for the intro tile to open, so
  // the beeps follow a real user gesture instead of being swallowed by autoplay.
  useEffect(() => {
    if (!terminalVisible || playedRef.current) return;
    playedRef.current = true;
    cancelRef.current = false;

    const delay = (ms: number) =>
      new Promise<void>(res => {
        const id = window.setTimeout(res, ms);
        // store for cleanup — we keep it simple with the cancel flag
        void id;
      });

    (async () => {
      await delay(600); // brief silence before we start

      for (let i = 0; i < steps.length; i++) {
        if (cancelRef.current) return;
        setLineIdx(i);
        setCharIdx(0);

        // type character by character
        for (let c = 1; c <= steps[i].text.length; c++) {
          if (cancelRef.current) return;
          await delay(CHAR_MS);
          setCharIdx(c);
        }

        // line complete — beep + checkmark
        if (!cancelRef.current) {
          if (!audioCtxRef.current) {
            try {
              audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
            } catch { /* noop */ }
          }
          if (audioCtxRef.current) playBeep(audioCtxRef.current, i === steps.length - 1);
          setChecked(prev => {
            const next = [...prev];
            next[i] = true;
            return next;
          });
          await delay(PAUSE_MS);
        }
      }

      if (!cancelRef.current) {
        await delay(BTN_DELAY);
        setShowBtn(true);
      }
    })();

    return () => {
      cancelRef.current = true;
    };
  }, [terminalVisible]); // playedRef keeps it single-run when the tile reopens

  // Skip — show everything instantly
  const skip = () => {
    cancelRef.current = true;
    setLineIdx(steps.length - 1);
    setCharIdx(steps[steps.length - 1].text.length);
    setChecked([true, true, true, true, true]);
    setShowBtn(true);
  };

  // Collapsing mid-animation would freeze a half-typed line, so snap to the
  // finished state on the way out.
  const toggleIntro = () => {
    if (introOpen && !showBtn) skip();
    setIntroOpen(!introOpen);
  };

  const terminalCard = (
    <div className="start-terminal">
      <div className="start-header">
        <span className="start-prompt">{'>'}</span>
        <span className="start-title">Bassmaster Workbench</span>
        <span className="start-version">v{packageJson.version}</span>
      </div>

      <ul className="start-steps">
        {steps.map((step, i) => {
          const isActive  = lineIdx === i;
          const isVisible = lineIdx >= i;
          const text      = isActive
            ? step.text.slice(0, charIdx)
            : isVisible ? step.text : '';
          return (
            <li key={i} className={`start-line ${isVisible ? 'start-line--visible' : ''}`}>
              <span className={`start-check ${checked[i] ? 'start-check--done' : ''}`}>
                {checked[i] ? '✓' : '○'}
              </span>
              <span className="start-line-text">
                {text}
                {isActive && !checked[i] && <span className="start-cursor">▋</span>}
              </span>
              {checked[i] && step.nav && (
                <button
                  className="start-nav-btn"
                  onClick={e => { e.stopPropagation(); onNavigate(step.nav!); }}
                >
                  →
                </button>
              )}
              {checked[i] && step.url && (
                <a
                  className="start-url-btn"
                  href={step.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={e => e.stopPropagation()}
                >
                  ☕
                </a>
              )}
            </li>
          );
        })}
      </ul>

      {showBtn && (
        <div className="start-cta-row">
          <button className="start-cta" onClick={() => onNavigate('learn-guitar')}>
            {t('game:start_cta')} →
          </button>
        </div>
      )}

      {!showBtn && lineIdx >= 0 && (
        <div className="start-skip-hint">{t('game:start_skip')}</div>
      )}
    </div>
  );

  return (
    <div className="start-screen" onClick={showBtn || !terminalVisible ? undefined : skip}>
      <div className="start-layout">
        {!isMobile && terminalCard}

        <aside className="start-tiles">
          {tiles.map((tile, i) =>
            tile.kind === 'intro' ? (
              <React.Fragment key="intro">
                <button
                  className={`start-tile start-tile--intro ${introOpen ? 'start-tile--open' : ''}`}
                  style={{ animationDelay: `${i * 80}ms` }}
                  aria-expanded={introOpen}
                  onClick={e => { e.stopPropagation(); toggleIntro(); }}
                >
                  <span className="start-tile-glyph" aria-hidden="true">{tile.glyph}</span>
                  <span className="start-tile-body">
                    <span className="start-tile-title">{tile.title}</span>
                    <span className="start-tile-desc">{tile.desc}</span>
                  </span>
                  <span className="start-tile-chevron" aria-hidden="true">▾</span>
                </button>
                {introOpen && terminalCard}
              </React.Fragment>
            ) : (
              <button
                key={tile.nav}
                className={`start-tile ${tile.nav === 'learn-guitar' ? 'start-tile--primary' : ''}`}
                style={{ animationDelay: `${i * 80}ms` }}
                onClick={e => { e.stopPropagation(); onNavigate(tile.nav); }}
              >
                <span className="start-tile-glyph" aria-hidden="true">{tile.glyph}</span>
                <span className="start-tile-body">
                  <span className="start-tile-title">{tile.title}</span>
                  <span className="start-tile-desc">{tile.desc}</span>
                </span>
                <span className="start-tile-arrow" aria-hidden="true">→</span>
              </button>
            ),
          )}
        </aside>
      </div>
    </div>
  );
}
